import { useState, type FormEvent } from "react";
import type { Currency } from "@core/index";
import { useAppStore, type SetupInput } from "@state/store";
import { useBrand } from "@branding/useBrand";

/**
 * First-run go-live wizard (ARCHITECTURE §3.4). A fresh production deploy starts
 * empty; this branded, single-screen form creates the organization — name, base
 * currency, fiscal year, and the first entity — before the app is usable. On
 * completion the store stamps a persisted `setupComplete` flag so it runs once.
 */
const CURRENCIES: { code: Currency; label: string }[] = [
  { code: "AED", label: "AED — UAE Dirham" },
  { code: "USD", label: "USD — US Dollar" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — Pound Sterling" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function SetupWizard() {
  const brand = useBrand();
  const completeSetup = useAppStore((s) => s.completeSetup);
  const error = useAppStore((s) => s.error);

  const [orgName, setOrgName] = useState("");
  const [baseCurrency, setBaseCurrency] = useState<Currency>("AED");
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(1);
  const [entityName, setEntityName] = useState("");
  const [entityKind, setEntityKind] = useState<SetupInput["entityKind"]>("freezone");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const initial = (brand.name.trim()[0] || "L").toUpperCase();
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ownerEmail.trim());
  const canSubmit =
    orgName.trim().length > 0 &&
    entityName.trim().length > 0 &&
    ownerName.trim().length > 0 &&
    emailValid &&
    password.length >= 8 &&
    !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    await completeSetup({
      orgName,
      baseCurrency,
      fiscalYearStartMonth,
      entityName,
      entityKind,
      ownerName,
      ownerEmail,
      password,
    });
    // On failure the store surfaces its error state; re-enable the form.
    setSubmitting(false);
  }

  return (
    <div className="setup">
      <main className="setup-panel">
        <div className="brand" aria-label={`${brand.name} setup`}>
          <div className="brand-mark" aria-hidden="true">
            {initial}
          </div>
          <div>
            <div className="brand-name">{brand.name}</div>
            <div className="brand-tag">{brand.tagline}</div>
          </div>
        </div>

        <div className="page-head mt5">
          <div>
            <h1>Welcome — let’s set up your organization</h1>
            <div className="sub">
              This runs once. It creates your organization, base currency, fiscal
              year, and first entity so your ledger starts clean.
            </div>
            <div className="sub" style={{ marginTop: "var(--s2)" }}>
              You’ll create the first owner account here. Your ledger is stored
              securely on the server and scoped to your organization — only
              signed-in members of your org can see it.
            </div>
          </div>
        </div>

        <form className="card mt4" onSubmit={onSubmit}>
          <div className="card-body">
            <div className="field">
              <label htmlFor="orgName">Organization name</label>
              <input
                id="orgName"
                className="input"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g. Meridian Group"
                autoFocus
              />
              <span className="hint">The legal or trading name of your group.</span>
            </div>

            <div className="row mt4" style={{ gap: "var(--s5)" }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="baseCur">Base currency</label>
                <select
                  id="baseCur"
                  className="input"
                  value={baseCurrency}
                  onChange={(e) => setBaseCurrency(e.target.value as Currency)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <span className="hint">All balances report in this currency.</span>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="fyStart">Fiscal year starts</label>
                <select
                  id="fyStart"
                  className="input"
                  value={fiscalYearStartMonth}
                  onChange={(e) => setFiscalYearStartMonth(Number(e.target.value))}
                >
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row mt4" style={{ gap: "var(--s5)" }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="entName">First entity</label>
                <input
                  id="entName"
                  className="input"
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
                  placeholder="e.g. Meridian Trading FZ-LLC"
                />
                <span className="hint">You can add more entities later.</span>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="entKind">Entity type</label>
                <select
                  id="entKind"
                  className="input"
                  value={entityKind}
                  onChange={(e) => setEntityKind(e.target.value as SetupInput["entityKind"])}
                >
                  <option value="freezone">Free Zone company</option>
                  <option value="mainland">Mainland company</option>
                </select>
              </div>
            </div>

            <div className="field mt5">
              <label htmlFor="ownerName">Your name (owner account)</label>
              <input
                id="ownerName"
                className="input"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="e.g. Aisha Rahman"
              />
              <span className="hint">
                You become the organization owner — you can invite members later
                from Settings.
              </span>
            </div>

            <div className="row mt4" style={{ gap: "var(--s5)" }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ownerEmail">Email</label>
                <input
                  id="ownerEmail"
                  className="input"
                  type="email"
                  autoComplete="username"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="you@company.com"
                />
                <span className="hint">You’ll sign in with this email.</span>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="ownerPassword">Password</label>
                <input
                  id="ownerPassword"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span className="hint">At least 8 characters.</span>
              </div>
            </div>

            {error ? (
              <div className="badge bad mt4" role="alert">
                <span className="dot" /> {error}
              </div>
            ) : null}

            <div className="mt5">
              <button type="submit" className="btn primary" disabled={!canSubmit}>
                {submitting ? "Creating…" : "Create organization"}
              </button>
            </div>
          </div>
        </form>

        <p className="hint mt4">{brand.legalFooter}</p>
      </main>
    </div>
  );
}
